// Lightweight UTM / click-id attribution helper.
// Captures marketing params from the current URL on first load and persists
// them in sessionStorage so we don't lose attribution on internal navigation.

const KEY = "bm_attribution_v1";

const PARAM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

export type AttributionData = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landing_page?: string;
};

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Call once early (e.g. in App bootstrap). Idempotent. */
export function captureAttribution(): AttributionData {
  if (typeof window === "undefined") return {};
  const storage = safeStorage();
  const existingRaw = storage?.getItem(KEY);
  if (existingRaw) {
    try {
      return JSON.parse(existingRaw) as AttributionData;
    } catch {
      /* fallthrough — rewrite */
    }
  }

  const url = new URL(window.location.href);
  const data: AttributionData = {};
  for (const key of PARAM_FIELDS) {
    const v = url.searchParams.get(key);
    if (v) data[key] = v.slice(0, 255);
  }
  const ref = document.referrer;
  if (ref && !ref.startsWith(window.location.origin)) {
    data.referrer = ref.slice(0, 500);
  }
  data.landing_page = window.location.href.slice(0, 500);

  try {
    storage?.setItem(KEY, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
  return data;
}

/** Read persisted attribution (does not re-parse URL). */
export function getAttribution(): AttributionData {
  const storage = safeStorage();
  const raw = storage?.getItem(KEY);
  if (!raw) return captureAttribution();
  try {
    return JSON.parse(raw) as AttributionData;
  } catch {
    return {};
  }
}